
import { defineComponent, PropType } from 'vue';
import { mapActions } from 'pinia';
import { type CartItem, type Item, WindcaveConfig } from '@white-label-types/parking-checkout';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentMerchantId } from '@white-label-helper/get-app-payment-merchant-id';
import { getAppPaymentProviderConfig } from '@white-label-helper/get-app-payment-provider-config';
import { PAYMENT_PROVIDERS, PaymentConfig, FEATURE_FLAGS } from '@white-label-configuration/constants';
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';
import { addCustomMetric } from '@white-label-helper/shared-helpers';
import getAirport from '@white-label-helper/mixin-get-airport';
import { poolingBooking } from '@white-label-helper/api-parkings-payment-config';
import { useEventBus } from '@white-label-helper/shared-composables';

const eventBus = useEventBus();

export default defineComponent({
  mixins: [getAirport],
  props: {
    paymentsConfig: {
      type: Object as PropType<WindcaveConfig>,
      required: true,
    },
    totalValue: {
      type: Number,
      required: true,
    },
    isFormInvalid: {
      type: Boolean,
      required: true,
    },
    cartItems: {
      type: Array as PropType<CartItem[] | Item[]>,
      default: () => [] as CartItem[] | Item[],
    },
  },

  data: () => ({
    showError: false,
    currency: getAppVariable('default_currency'),
    windcaveConfig: {
      googlePayBusinessId: '',
      googlePayMerchantId: '',
      applePayMerchantId: '',
    },
    transactionStartTime: performance.now(),
    chosenPaymentMethod: '',
  }),

  computed: {
    isApplePayEnabled(): boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.IMP_1275_WINDCAVE_ENABLE_APPLE_PAY) && Boolean(this.windcaveConfig.applePayMerchantId);
    },
    isGooglePayEnabled(): boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.IMP_1275_WINDCAVE_ENABLE_GOOGLE_PAY) && Boolean(this.windcaveConfig.googlePayMerchantId);
    },
    isMobileMenuEnabled(): boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.IMP_1300_WINDCAVE_ENABLE_MOBILE_MENU);
    },
    isMobilePaymentEnabled(): boolean {
      return this.isGooglePayEnabled || this.isApplePayEnabled;
    },
    /**
     * Retrieves the brand color in RGBA format from the partner configuration.
     */
    brandColour(): string {
      return getAppVariable('colours.brand') || getAppVariable('brand_color', '');
    },

    product(): CartItem | {} {
      return this.cartItems?.length > 0 ? this.cartItems[0] : {};
    },

    /**
     * Computes the merchant name using the airport details from the get-app-airport mixin.
     * Formats it as "Airport Name (Airport Code)".
     */
    merchantName(): string {
      return `${this.airport.name} (${this.airport.code})`;
    },
  },

  mounted(): void {
    eventBus.$on('windcaveHandleReload', this.handleWindcaveError);
    this.populateWindcaveConfig();
  },

  beforeDestroy (): void {
    eventBus.$off('windcaveHandleReload', this.handleWindcaveError);
  },

  methods: {
    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
    }),

    async handleWindcaveTransaction() {
      addCustomMetric("tracking", "Payment Metric: PreAuth", {
        partner_id: getAppVariable('partner_id'),
        provider: PAYMENT_PROVIDERS.WINDCAVE,
        partner_name: `${getAppVariable('partner_id')}-${PAYMENT_PROVIDERS.WINDCAVE}`
      }, this.transactionStartTime, 'payment_service');

      /**
       * We can time out waiting for windcave,
       * so send a request to the back end to check status before moving on to
       * the next step in a payment process
       */
      poolingBooking(this.paymentsConfig.sessionId, 1000 * 5)
        .then(() => {
          // Only do this when we have an "Authorized" status
          this.$emit('payment-completed', {
            payment_method_id: this.paymentsConfig.sessionId,
            merchant_id: getAppPaymentMerchantId(),
          });
        })
        .catch((error) => {
          this.$emit('error');
          this.handleWindcaveError(true);
          addCustomMetric("error", "Payment Metric: PreAuth", {
            partner_id: getAppVariable('partner_id'),
            provider: PAYMENT_PROVIDERS.WINDCAVE,
            partner_name: `${getAppVariable('partner_id')}-${PAYMENT_PROVIDERS.WINDCAVE}`,
            error: error.message,
          },
            this.transactionStartTime,
            'payment_service'
          );
        });
    },

    /**
     * Handles the Windcave payment error event by showing the error message
     * and reinitializing the Windcave payment form, allowing the user to retry the payment.
     */
    handleWindcaveError(shouldShowError: boolean) {
      this.hideLoadingScreen();

      if (shouldShowError) {
        this.showError = true;
      }

      this.$emit('windcaveWidgetStartedLoading');
      this.$refs.payment?.createWindcavePaymentForm();
    },

    /**
     * Triggers the submit button inside 'windcave-ui' custom element.
     * This button is hidden with data-basic-styling property.
     */
    submitForm() {
      const btn: HTMLButtonElement | null = document.querySelector('#windcave-ui__submit');
      btn?.click();

      this.transactionStartTime = performance.now();
    },

    populateWindcaveConfig() {
      const activePaymentProvider: PaymentConfig | null = getAppPaymentProviderConfig();
      if (activePaymentProvider) {
        this.windcaveConfig.googlePayBusinessId = activePaymentProvider.config?.payment_google_business_id;
        this.windcaveConfig.googlePayMerchantId = activePaymentProvider.config?.payment_google_merchant_id;
        this.windcaveConfig.applePayMerchantId = activePaymentProvider.config?.payment_apple_merchant_id;
      }
    },

    /**
     * Hides the loading screen.
     */
    hideLoadingScreen() {
      this.commitUpdatePreloader('');
    },
    
    /**
     * Triggered by the `windcaveTransactionCancelled` event from the payment widget
     * when the user closes the Google Pay/Apple Pay modal, 3DS authentication modal, etc.
     *
     * We check if the cancelled transaction's payment method matches
     * the currently chosen payment method, to fix an issue of Windcave Drop-In component
     * where a user cancels Apple Pay and then attempts to pay with Google Pay
     */
    onTransactionCancel(event: CustomEvent): void {
      const paymentMethod = event.detail;
      
      if (paymentMethod === this.chosenPaymentMethod) {
        this.hideLoadingScreen();
      }
    },
    
    /**
     * Handler for the `windcaveTransactionStarted` event from the windcave-ui web component,
     * This is called before payment authorization to validate the form for Apple and Google Pay.
     */
    onPaymentStart(event: CustomEvent): void {
      // Payment methods that render their own pay button outside the main form submit flow
      const WALLET_PAYMENT_METHODS = new Set(['googlePay', 'applePay'] as const);
      
      const { paymentMethod, next, cancel } = event.detail;
      
      this.chosenPaymentMethod = paymentMethod;
      
      if (WALLET_PAYMENT_METHODS.has(paymentMethod) && this.isFormInvalid) {
        this.$emit('show-validation-errors');
        cancel();
      } else {
        next();
        this.commitUpdatePreloader(this.$t('UI.fullscreenPreloaderMessages.nearlyThere'));
      }
    },
  },
})
